/**
 * Timbres de llamada, sintetizados con WebAudio.
 *
 * Se sintetizan en vez de reproducir un mp3 por dos razones: no mete binarios
 * en el repo, y sobre todo esquiva el bloqueo de autoplay — un oscilador
 * arranca sin gesto previo del usuario donde un <audio> con `src` se quedaría
 * mudo hasta el primer clic, que en una llamada entrante es exactamente cuando
 * no puede fallar.
 *
 * Para cambiar el timbre del CRM basta con tocar TONO_ACTIVO.
 */

export type NombreTono =
    | 'clasico'
    | 'movil'
    | 'campana'
    | 'suave'
    | 'urgente'
    | 'dingdong'
    | 'arpa'
    | 'descendente'
    | 'burbuja'
    | 'retro'
    | 'campana-insistente'
    | 'campana-intensa'
    | 'saliente'
    | 'saliente-corto';

/** El que suena cuando ENTRA una llamada. */
export const TONO_ACTIVO: NombreTono = 'campana-insistente';

/**
 * El que oye el agente MIENTRAS ESPERA a que le contesten, en una llamada que
 * hacemos nosotros. Es un papel distinto del timbre de entrada: no compite por
 * tu atención —ya estás mirando la pantalla— sino que te dice que la línea sigue
 * viva. Por eso es un tono único, largo y más bajo de volumen, en lugar de una
 * melodía.
 *
 * PENDIENTE: todavía no suena en ninguna parte. Las llamadas salientes son la
 * Fase 2 y aún no existen; esto queda listo para cuando el modal tenga estado
 * "llamando".
 */
export const TONO_SALIENTE: NombreTono = 'saliente-corto';

interface Nota {
    /** Frecuencias que suenan a la vez (un acorde si hay más de una). */
    hz: number[];
    /**
     * Si está, la nota NO es fija: se desliza desde `hz` hasta aquí a lo largo
     * de su duración. Es lo que da el carácter de "burbuja" o de chirrido
     * ascendente que no se consigue encadenando notas sueltas.
     */
    hzFin?: number[];
    /** Segundos desde el inicio del ciclo. */
    en: number;
    /** Duración del sonido sostenido, sin contar la cola. */
    dur: number;
    /** 0–1, relativo al volumen del tono. */
    vol?: number;
}

interface Tono {
    descripcion: string;
    /** Cada cuántos segundos se repite el patrón. */
    ciclo: number;
    onda: OscillatorType;
    /**
     * `sostenida` mantiene el volumen y lo cierra (timbre de teléfono).
     * `percusiva` cae sola desde el ataque (campana, marimba).
     */
    envolvente: 'sostenida' | 'percusiva';
    /** Segundos de subida. Largo = entra suave, corto = entra seco. */
    ataque: number;
    volumen: number;
    notas: Nota[];
}

export const TONOS: Record<NombreTono, Tono> = {
    clasico: {
        descripcion: 'Teléfono de toda la vida (440 + 480 Hz)',
        ciclo: 2.5,
        onda: 'sine',
        envolvente: 'sostenida',
        ataque: 0.02,
        volumen: 0.5,
        notas: [
            { hz: [440, 480], en: 0, dur: 0.4 },
            { hz: [440, 480], en: 0.6, dur: 0.4 },
        ],
    },

    movil: {
        descripcion: 'Móvil moderno: dos notas que suben, repetidas',
        ciclo: 2.2,
        onda: 'triangle',
        envolvente: 'percusiva',
        ataque: 0.01,
        volumen: 0.45,
        notas: [
            { hz: [880], en: 0, dur: 0.13 },
            { hz: [1108.73], en: 0.17, dur: 0.13 },
            { hz: [880], en: 0.42, dur: 0.13 },
            { hz: [1108.73], en: 0.59, dur: 0.13 },
        ],
    },

    campana: {
        descripcion: 'Campanilla: tres notas ascendentes, tipo marimba',
        ciclo: 3,
        onda: 'sine',
        envolvente: 'percusiva',
        ataque: 0.008,
        volumen: 0.55,
        notas: [
            { hz: [659.25], en: 0, dur: 0.75 },      // Mi5
            { hz: [830.61], en: 0.14, dur: 0.75 },   // Sol#5
            { hz: [987.77], en: 0.28, dur: 1.0 },    // Si5
        ],
    },

    suave: {
        descripcion: 'Suave: una nota cálida que entra y sale sin sobresaltos',
        ciclo: 3.4,
        onda: 'sine',
        envolvente: 'sostenida',
        ataque: 0.35,
        volumen: 0.4,
        notas: [{ hz: [523.25, 783.99], en: 0, dur: 1.3 }],
    },

    urgente: {
        descripcion: 'Urgente: pitidos cortos y seguidos, difícil de ignorar',
        ciclo: 1.6,
        onda: 'square',
        envolvente: 'percusiva',
        ataque: 0.005,
        volumen: 0.25, // la onda cuadrada es MUCHO más ruidosa a igual amplitud
        notas: [
            { hz: [1046.5], en: 0, dur: 0.09 },
            { hz: [1046.5], en: 0.15, dur: 0.09 },
            { hz: [1046.5], en: 0.3, dur: 0.09 },
        ],
    },

    dingdong: {
        descripcion: 'Timbre de puerta: din-don, dos notas separadas y largas',
        ciclo: 3.2,
        onda: 'sine',
        envolvente: 'percusiva',
        ataque: 0.01,
        volumen: 0.55,
        notas: [
            // Sol#5 y Mi5: la tercera mayor descendente del timbre de casa.
            { hz: [830.61], en: 0, dur: 1.1 },
            { hz: [659.25], en: 0.55, dur: 1.4 },
        ],
    },

    arpa: {
        descripcion: 'Arpa: cinco notas que suben rápido, como un barrido',
        ciclo: 3.2,
        onda: 'triangle',
        envolvente: 'percusiva',
        ataque: 0.006,
        volumen: 0.4,
        notas: [
            { hz: [523.25], en: 0, dur: 0.9 },     // Do5
            { hz: [659.25], en: 0.07, dur: 0.9 },  // Mi5
            { hz: [783.99], en: 0.14, dur: 0.9 },  // Sol5
            { hz: [1046.5], en: 0.21, dur: 1.0 },  // Do6
            { hz: [1318.5], en: 0.28, dur: 1.3 },  // Mi6
        ],
    },

    descendente: {
        descripcion: 'Descendente: tres notas que bajan; el menos alarmante',
        ciclo: 3.4,
        onda: 'sine',
        envolvente: 'percusiva',
        ataque: 0.01,
        volumen: 0.55,
        notas: [
            // Lo contrario de `campana`. Una melodía que baja se lee como algo
            // que se posa, no como una alerta que escala.
            { hz: [987.77], en: 0, dur: 0.8 },
            { hz: [830.61], en: 0.16, dur: 0.8 },
            { hz: [659.25], en: 0.32, dur: 1.2 },
        ],
    },

    burbuja: {
        descripcion: 'Burbuja: dos toques cortos que se deslizan hacia arriba',
        ciclo: 2.4,
        onda: 'sine',
        envolvente: 'percusiva',
        ataque: 0.004,
        volumen: 0.5,
        notas: [
            { hz: [587.33], hzFin: [880], en: 0, dur: 0.16 },
            { hz: [739.99], hzFin: [1108.73], en: 0.2, dur: 0.22 },
        ],
    },

    // ── Variantes de `campana` con más presencia ────────────────────────────
    // Lo que hace que un timbre se sienta insistente no es subir el volumen:
    // es reducir el silencio entre repeticiones. Estas dos acortan el ciclo y
    // repiten el arpegio dentro de él, en vez de gritar más.

    'campana-insistente': {
        descripcion: 'Campanilla insistente: el mismo arpegio, dos veces por ciclo',
        ciclo: 2.4,
        onda: 'sine',
        envolvente: 'percusiva',
        ataque: 0.008,
        volumen: 0.6,
        notas: [
            { hz: [659.25], en: 0, dur: 0.6 },
            { hz: [830.61], en: 0.13, dur: 0.6 },
            { hz: [987.77], en: 0.26, dur: 0.8 },
            { hz: [659.25], en: 0.85, dur: 0.6 },
            { hz: [830.61], en: 0.98, dur: 0.6 },
            { hz: [987.77], en: 1.11, dur: 0.9 },
        ],
    },

    'campana-intensa': {
        descripcion: 'Campanilla intensa: cuatro notas, timbre más brillante, sin apenas pausa',
        ciclo: 2.2,
        // Triangular en vez de senoidal: tiene más armónicos, así que corta
        // mejor por encima del ruido de una oficina sin sonar a alarma.
        onda: 'triangle',
        envolvente: 'percusiva',
        ataque: 0.006,
        volumen: 0.5,
        notas: [
            { hz: [659.25], en: 0, dur: 0.5 },
            { hz: [830.61], en: 0.12, dur: 0.5 },
            { hz: [987.77], en: 0.24, dur: 0.5 },
            { hz: [1318.5], en: 0.36, dur: 0.7 },   // Mi6: la octava, que es lo que le da el filo
            { hz: [659.25], en: 0.8, dur: 0.5 },
            { hz: [830.61], en: 0.92, dur: 0.5 },
            { hz: [987.77], en: 1.04, dur: 0.5 },
            { hz: [1318.5], en: 1.16, dur: 0.9 },
        ],
    },

    // ── Espera de llamada saliente ──────────────────────────────────────────

    saliente: {
        descripcion: 'Saliente: un tono largo cada varios segundos, como el de un teléfono llamando',
        // 4.5 s de ciclo con 1.5 s de tono es la proporción de un ringback real:
        // mucho más silencio que sonido. Con un ciclo corto, esperar 20 s a que
        // contesten se hace insoportable.
        ciclo: 4.5,
        onda: 'sine',
        envolvente: 'sostenida',
        ataque: 0.03,
        // Bajo a propósito: esto suena junto al oído del agente durante toda la
        // espera, no es un aviso que tenga que llamar su atención.
        volumen: 0.28,
        notas: [{ hz: [440, 480], en: 0, dur: 1.5 }],
    },

    'saliente-corto': {
        descripcion: 'Saliente corto: tono único de 425 Hz, al estilo europeo/latinoamericano',
        ciclo: 3.5,
        onda: 'sine',
        envolvente: 'sostenida',
        ataque: 0.03,
        volumen: 0.28,
        notas: [{ hz: [425], en: 0, dur: 1.0 }],
    },

    retro: {
        descripcion: 'Retro: arpegio de consola de 8 bits',
        ciclo: 2.4,
        onda: 'square',
        envolvente: 'percusiva',
        ataque: 0.004,
        volumen: 0.16, // cuadrada: mucho más ruidosa a igual amplitud
        notas: [
            { hz: [523.25], en: 0, dur: 0.1 },
            { hz: [659.25], en: 0.1, dur: 0.1 },
            { hz: [783.99], en: 0.2, dur: 0.1 },
            { hz: [1046.5], en: 0.3, dur: 0.28 },
            { hz: [783.99], en: 0.6, dur: 0.1 },
            { hz: [1046.5], en: 0.7, dur: 0.38 },
        ],
    },
};

/**
 * Programa UN ciclo del tono y devuelve los osciladores creados.
 *
 * Cada ciclo usa nodos nuevos: los OscillatorNode son de un solo uso por diseño
 * —una vez parados no se reinician— y crearlos es barato. El que llama debe
 * guardarlos para poder cortarlos si la llamada termina a media nota.
 */
export function reproducirCiclo(ctx: AudioContext, tono: Tono): OscillatorNode[] {
    const t0 = ctx.currentTime;
    const creados: OscillatorNode[] = [];

    for (const nota of tono.notas) {
        const inicio = t0 + nota.en;
        const pico = tono.volumen * (nota.vol ?? 1);
        // Repartir el volumen entre las voces del acorde evita que dos o tres
        // notas simultáneas saturen y suenen a distorsión.
        const porVoz = pico / nota.hz.length;

        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0, inicio);
        gain.gain.linearRampToValueAtTime(porVoz, inicio + tono.ataque);

        let fin: number;
        if (tono.envolvente === 'percusiva') {
            // Caída exponencial: es como decae un cuerpo que vibra, y por eso
            // suena a instrumento en vez de a zumbador.
            fin = inicio + nota.dur;
            gain.gain.exponentialRampToValueAtTime(0.0001, fin);
        } else {
            fin = inicio + nota.dur;
            gain.gain.setValueAtTime(porVoz, fin - tono.ataque);
            gain.gain.linearRampToValueAtTime(0, fin);
        }

        nota.hz.forEach((hz, i) => {
            const osc = ctx.createOscillator();
            osc.type = tono.onda;
            osc.frequency.setValueAtTime(hz, inicio);

            const destino = nota.hzFin?.[i];
            if (destino) {
                // Exponencial y no lineal: el oído percibe la altura en escala
                // logarítmica, así que una rampa lineal en Hz suena como si el
                // deslizamiento frenara al final.
                osc.frequency.exponentialRampToValueAtTime(destino, fin);
            }

            osc.connect(gain);
            osc.start(inicio);
            osc.stop(fin + 0.05);
            creados.push(osc);
        });
    }

    return creados;
}
